'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X, ExternalLink } from '@/lib/icons';
import { CATEGORIES, TOPICS } from '@/lib/help-content';
import { cn } from '@/lib/utils';

function SidebarContent({ pathname, onSelect }) {
  return (
    <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
      {CATEGORIES.map((cat) => {
        const Icon = cat.icon;
        return (
          <div key={cat.id}>
            <div className="flex items-center gap-2 px-2 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Icon className="w-3.5 h-3.5" />
              {cat.label}
            </div>
            <ul className="space-y-0.5">
              {cat.topics.map((slug) => {
                const topic = TOPICS[slug];
                if (!topic) return null;
                const href = `/help/${slug}`;
                const active = pathname === href || (slug === 'welcome' && pathname === '/help');
                return (
                  <li key={slug}>
                    <Link
                      href={slug === 'welcome' ? '/help' : href}
                      onClick={onSelect}
                      className={cn(
                        'block px-3 py-1.5 rounded-lg text-sm transition-colors',
                        active
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                      )}
                    >
                      {topic.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

export default function HelpShell({ children }) {
  const pathname = usePathname();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 flex items-center justify-between px-4 h-14 border-b border-border bg-background/95 backdrop-blur">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="md:hidden w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link href="/help" className="font-bold text-lg">
            OCTNE <span className="text-muted-foreground font-medium">Help</span>
          </Link>
        </div>
        <a
          href="https://octne.com"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Back to OCTNE
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </header>

      <div className="flex flex-1">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex md:flex-col w-72 shrink-0 border-r border-border sticky top-14 h-[calc(100vh-3.5rem)]">
          <SidebarContent pathname={pathname} />
        </aside>

        {/* Mobile drawer */}
        {drawerOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <aside className="absolute inset-y-0 left-0 w-72 bg-background border-r border-border flex flex-col">
              <div className="flex items-center justify-between h-14 px-4 border-b border-border shrink-0">
                <span className="font-bold">OCTNE Help</span>
                <button
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close menu"
                  className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <SidebarContent pathname={pathname} onSelect={() => setDrawerOpen(false)} />
            </aside>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="max-w-3xl mx-auto px-6 md:px-12 py-10">{children}</div>
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-6 px-6 md:px-12 mt-auto">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} OCTNE. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link href="/help/contact" className="hover:text-foreground transition-colors">
              Contact
            </Link>
            <a href="https://octne.com/terms" className="hover:text-foreground transition-colors">
              Terms
            </a>
            <a href="https://octne.com/privacy" className="hover:text-foreground transition-colors">
              Privacy
            </a>
            <a href="https://octne.com/returns" className="hover:text-foreground transition-colors">
              Returns
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
